import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Grid, Chip, Divider, Button, CircularProgress,
  Stack, Alert, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Tooltip,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { getMiSuscripcion, cancelarMiSuscripcion } from "../../componentes/http/actions/MiSuscripcionAction";

const formatFecha = (value) =>
  value ? new Date(value).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" }) : "—";

const capitalizar = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Color del chip de estado
const colorEstado = (estado) => {
  switch (estado) {
    case "activa": return "success";
    case "demo": return "info";
    case "pendiente_pago": return "warning";
    case "cancelada": return "default";
    case "suspendida": return "error";
    default: return "default";
  }
};

const etiquetaEstado = (estado) => {
  const map = {
    activa: "Activa",
    demo: "Demo",
    pendiente_pago: "Pago pendiente",
    cancelada: "Cancelada",
    suspendida: "Suspendida",
  };
  return map[estado] || estado;
};

const MiSuscripcion = () => {
  const token = localStorage.getItem("token");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [mensajeOk, setMensajeOk] = useState("");

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    getMiSuscripcion(token)
      .then((res) => {
        if (res?.success) setData(res);
        else setError(res?.message || "No se pudo cargar tu suscripción.");
      })
      .catch(() => setError("No se pudo cargar tu suscripción."))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleCancelar = async () => {
    setCancelando(true);
    try {
      const res = await cancelarMiSuscripcion(token, data.cancelToken);
      if (res?.success) {
        setMensajeOk(res.message || "Suscripción cancelada.");
        setConfirmOpen(false);
        cargar(); // refrescar estado
      } else {
        setError(res?.message || "No se pudo cancelar la suscripción.");
        setConfirmOpen(false);
      }
    } catch {
      setError("No se pudo cancelar la suscripción.");
      setConfirmOpen(false);
    } finally {
      setCancelando(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={{ pt: 2, pb: 4, maxWidth: 800, mx: "auto" }}>
        <Alert severity="info">{error}</Alert>
      </Box>
    );
  }

  //const esPago = data.plan === "mensual" || data.plan === "anual";
  const puedeCancel = data.estado !== "cancelada" && !!data.cancelToken;

  return (
    <Box sx={{ pt: { xs: 2.5, sm: 1.5 }, pb: 4, maxWidth: 900, mx: "auto" }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5">Mi suscripción</Typography>
        <Typography variant="body2" color="text.secondary">
          Revisa el estado de tu plan y la configuración de tus alertas.
        </Typography>
      </Box>

      {mensajeOk && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMensajeOk("")}>
          {mensajeOk}
        </Alert>
      )}
      {error && data && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Tarjeta principal del plan */}
      <Paper elevation={1} sx={{ p: { xs: 2.5, sm: 3 }, mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <WorkspacePremiumOutlinedIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                Plan {capitalizar(data.plan)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Frecuencia de alertas: {capitalizar(data.frecuencia)}
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={etiquetaEstado(data.estado)}
            color={colorEstado(data.estado)}
            sx={{ fontWeight: 600 }}
          />
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarTodayOutlinedIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Inicio</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatFecha(data.fechaInicio)}</Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <EventBusyOutlinedIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {data.estado === "cancelada" ? "Válida hasta" : "Vence"}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatFecha(data.fechaFin)}</Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutorenewOutlinedIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Estado</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {data.vigenteAhora
                    ? (data.diasRestantes != null ? `Vigente · ${data.diasRestantes} días restantes` : "Vigente")
                    : "No vigente"}
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Configuración de alertas */}
      <Paper elevation={1} sx={{ p: { xs: 2.5, sm: 3 }, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Configuración de tus alertas
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Países</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {data.paises?.length ? data.paises.map((p) => (
            <Chip key={p} label={p} size="small" variant="outlined" />
          )) : <Typography variant="body2" color="text.secondary">—</Typography>}
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Reguladores</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {data.reguladores?.length ? data.reguladores.map((r) => (
            <Chip key={r} label={r} size="small" variant="outlined" />
          )) : <Typography variant="body2" color="text.secondary">Todos</Typography>}
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Rubros</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {data.rubros?.length ? data.rubros.map((r) => (
            <Chip key={r} label={r} size="small" variant="outlined" color="primary" />
          )) : <Typography variant="body2" color="text.secondary">Todos</Typography>}
        </Stack>
      </Paper>

      {/* Acciones de plan */}
      <Paper elevation={1} sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Gestionar plan
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          {/* Fase 2: botones deshabilitados como "Próximamente", igual que el landing */}
          <Tooltip title="Disponible próximamente">
            <span>
              <Button variant="contained" disabled>
                {data.plan === "demo" ? "Pasar a plan de pago" : "Cambiar plan"}
              </Button>
            </span>
          </Tooltip>

          {puedeCancel && (
            <Button variant="outlined" color="error" onClick={() => setConfirmOpen(true)}>
              Cancelar suscripción
            </Button>
          )}
        </Stack>

        {data.estado === "cancelada" && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Tu suscripción está cancelada. Seguirás viendo las normas de tu período hasta la fecha de vencimiento.
          </Alert>
        )}
      </Paper>

      {/* Diálogo de confirmación de cancelación */}
      <Dialog open={confirmOpen} onClose={() => !cancelando && setConfirmOpen(false)}>
        <DialogTitle>¿Cancelar tu suscripción?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Si cancelas, dejarás de recibir alertas nuevas. Seguirás teniendo acceso a las
            normas de tu período actual hasta la fecha de vencimiento
            {data.fechaFin ? ` (${formatFecha(data.fechaFin)})` : ""}. Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={cancelando}>
            Volver
          </Button>
          <Button onClick={handleCancelar} color="error" variant="contained" disabled={cancelando}>
            {cancelando ? <CircularProgress size={22} color="inherit" /> : "Sí, cancelar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MiSuscripcion;